import { z } from "zod";
import { Button, Group, Modal, PasswordInput } from "@mantine/core";
import { useCallback, useMemo, type FC } from "react";
import { useTRPC } from "../../../trpc";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { FormattedMessage, useIntl } from "react-intl";
import { passwordMismatch, passwordSchemaStrength } from "../../../utils/schema";
import { PasswordStrength } from "../../../components/PasswordStrength";
import { useSetUser } from "../../../hooks/user";
import { useMutation } from "@tanstack/react-query";
import { useMapkaNotification } from "../../../hooks/notifications.js";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const defaultFormValues = {
  password: "",
  passwordConfirmation: "",
};

export const SetupPasswordModal: FC<ChangePasswordModalProps> = ({ open, onClose }) => {
  const setUser = useSetUser();
  const intl = useIntl();
  const trpc = useTRPC();
  const { mutate: setupPassword, isPending } = useMutation(trpc.setupPassword.mutationOptions());
  const mapkaNotification = useMapkaNotification();

  const validate = useMemo(() => zod4Resolver(
    z.object({
      password: passwordSchemaStrength(intl),
      passwordConfirmation: passwordSchemaStrength(intl),
    }).refine(data => data.password === data.passwordConfirmation, {
      message: passwordMismatch(intl),
    }),
  ), [intl]);

  const { onSubmit, getInputProps, isValid, values } = useForm({
    validateInputOnBlur: true,
    validate,
    initialValues: defaultFormValues,
  });

  const handleChangePassword = useCallback((form: typeof defaultFormValues) => {
    setupPassword(form, {
      onSuccess: (user) => {
        setUser(user);
        onClose();
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "Password established",
            id: "notification.passwordSetup",
          }),
          message: intl.formatMessage({
            defaultMessage: "You can now login using your email and password",
            id: "notification.passwordSetupMessage",
          }),
          variant: "success",
        });
      },
    });
  }, [setupPassword, intl, onClose]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={intl.formatMessage({
        defaultMessage: "Setup password",
        id: "title.setupPassword",
      })}
    >
      <form onSubmit={onSubmit(handleChangePassword)}>
        <PasswordInput
          label={intl.formatMessage({
            defaultMessage: "Password",
            id: "label.password",
          })}
          autoComplete="new-password"
          {...getInputProps("password")}
        />
        <PasswordInput
          label={intl.formatMessage({
            defaultMessage: "Confirm password",
            id: "label.confirmPassword",
          })}
          autoComplete="new-password"
          {...getInputProps("passwordConfirmation")}
        />
        <PasswordStrength {...values} />

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button type="reset" loading={isPending} onClick={onClose} variant="primary">
            <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" loading={isPending} disabled={!isValid()}>
            <FormattedMessage id="button.save" defaultMessage="Save" />
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
